import './link_creator.less';

import PropTypes from 'prop-types';
import autobind from 'class-autobind';

import React from 'react';
import extend from 'lodash/extend';
import isEmpty from 'lodash/isEmpty';
import size from 'lodash/size';

import Page from 'zoomforth_zf/models/page.js';
import Route from 'zoomforth_zf/models/route.js';
import RouteCollection from 'zoomforth_zf/models/route.collection.js';

import AskForVisitorInfoToggleSection from './ask_for_visitor_info_toggle_section.jsx';
import NotificationsToggleSection from './notifications_toggle_section.jsx';

import Button from 'zoomforth_component_lib/btn_basic.jsx';
import LinkPathEditor from 'zoomforth_component_lib/link_path_editor.jsx';

import userStore from 'zoomforth_zf/appglobals/user.js';
import accountStore from 'zoomforth_zf/appglobals/account.js';
import routesByPageStore from 'zoomforth_zf/appglobals/routes-by-page.js';



class LinkCreator extends React.Component {

  constructor(props) {
    super(props);

    this.displayName = 'LinkCreator';
    this.domainAndPathEditor = null;

    this.state = {
      askForVisitorInfo: props.initialAskForVisitorInfo,
      sendNotifications: props.initialSendNotifications,

      path: '',
      selectedDomainId: accountStore.get().get('default_domain_id'),
      selectedDomainUrl: accountStore.get().getDefaultDomainUrl(),

      errors: {}
    };

    autobind(this);
  }

  componentDidMount() {
    this.domainAndPathEditor.focus();

    RouteCollection.findValidNewRoutePath(
      accountStore.get().getDefaultDomainUrl(),
      this.props.page.get('unpublishedName').replace(/[^a-zA-Z0-9]+/g,'-').toLowerCase()
    )
      .done((foundPath)=> {
        this.setState(({ path, ...state })=> ({
          ...state,
          // If the user has typed something already (path !== '') then ignore the 
          // foundpath (It's only a suggestion after all)
          path: isEmpty(path) ? foundPath : path
        }));
      });
  }

  render() {
    const pageRoutes = routesByPageStore.get(this.props.page.id);
    const { domains } = this.props;

    return (
      <div className="link-creator">

        <div className="body">

          <div className="section">

            {
              (this.props.showShareHelpText && pageRoutes.length > 0) ?
              <p className="share-link-help-text">
                You are creating a new link for this site, which already 
                has <strong>{pageRoutes.length}</strong> { pageRoutes.length === 1 ? 'link' : 'links' }.
                Click <span className="link" onClick={() => { this.props.onViewLinksClick(this.props.page); }}>here</span> to view and share existing links or create a new
                link below:
              </p>
              :
              null
            }

            <LinkPathEditor 
              ref={(domainAndPathEditor) => { this.domainAndPathEditor = domainAndPathEditor; }}
              domainId={this.state.selectedDomainId}
              domainUrl={this.state.selectedDomainUrl}
              domainsForAccount={domains ? domains.models : []}
              path={this.state.path}
              error={this.state.errors.url} 
              onEnterKeyUp={this.handleCreateLink}
              onPathChange={
                (path)=> { this.setState({path:path}); }
              }
              onDomainChange={({ domainId, domainUrl })=> {
                this.setState({
                  selectedDomainId: domainId,
                  selectedDomainUrl: domainUrl
                });
              }}
            />


            <div className="buttons-container">
              <Button type="secondary" onClick={this.props.onRequestClose}>
                Cancel
              </Button>
              <Button
                onClick={this.handleCreateLinkClick}>
                Create Link
              </Button>
            </div>

          </div>


          <div className="link-settings" key="link-settings">
            <div className="section">
              <div className="section-header">
                Settings
              </div>

              <AskForVisitorInfoToggleSection
                checked={this.state.askForVisitorInfo}
                disabled={this.props.page.privacySettingKnowsIdentity()}
                disabledReason={
                  this.props.page.usesSAMLPrivacy() ?
                    'This site is protected by SAML authentication. Visitors will be tracked with the name and email provided by the SAML provider.'
                  : this.props.page.usesEmailPrivacy() ?
                    'This site is protected by email authentication. Visitors will be tracked with the email they use to view the page.'
                  :
                    null
                }
                onChange={this.handleAskForVisitorInfoChange}
                areVisitsFromEuAnonymous={accountStore.get().get('tracking_requirement_for_eu') == 'anonymize'}
                areVisitsFromCaliforniaAnonymous={accountStore.get().get('tracking_requirement_for_california') == 'anonymize'}
                areVisitsOutsideEuAnonymous={accountStore.get().get('tracking_requirement_outside_eu') == 'anonymize'}
              />

              <NotificationsToggleSection 
                emailAddress={userStore.get().get('email')}
                checked={this.state.sendNotifications} 
                onChange={this.handleSendNotificationsChange} 
              />
            </div>
          </div>
        </div> 

      </div>
    );
  }

  // Set all of the error states. 
  //  i.e., this.setState({ errors: {foo:..., bar:...}})
  //
  // Return `true` if the modal inputs are all valid. That is, that there were no error 
  // states set.
  //
  validateInputs() {

    var errors = {};

    // Basic validations

    if (!this.state.path) {
      extend(errors, {
        url: 'Link cannot be empty.'
      });
    }

    this.setState({
      errors: errors
    });

    return !(size(errors) > 0); // Return `false` if there are errors 

  }

  handleCreateLink() {

    if (!this.validateInputs()) {
      // this.state.errors should be populated now
      return;
    }

    var data = {
      visitCount: 0, // We know the visit count is 0 because this is a new model!
      domain_id: this.state.selectedDomainId,
      route_path: this.state.path,
      resource_id: this.props.page.id,
      creation_method: 'links_dashboard'
    };

    if (this.state.askForVisitorInfo) {
      data.ask_for_email = true;
      data.ask_for_name = true;
    }
    
    if (this.state.sendNotifications) {
      data.notify_trigger = 'on_every_visit';
    }

    var newRoute = new Route(data);
    const potentialNewUrl = this.state.selectedDomainUrl + '/' + data.route_path;

    RouteCollection.isValidNewRouteUrl(potentialNewUrl)

      .then(() => {

        newRoute.save()

          .then(()=> {
            // We have to refetch the page instance since there are times where we
            // are setting the canonical URL and we won't have it in this context
            this.props.page.fetch().done(() => {
              // Note on slight lameness onRouteCreate needs to add the route to
              // its collection so that the route can hydrate its domain
              // data from it's domain id
              if (this.props.onRouteCreate) { 
                this.props.onRouteCreate(newRoute, this.props.page);
              }

              if (this.state.shouldSaveAsDefault) {
                this.props.onInitialSettingsSave({
                  initialSendNotifications: this.state.sendNotifications,
                  initialAskForVisitorInfo: this.state.askForVisitorInfo
                });            
              }

              if (this.state.sendNotifications) {
                this.props.onSubscribeToRouteClick(newRoute.id);
              }

            });
          });

      })

      .fail((errorMessage)=> {

        this.setState({
          errors: { url: errorMessage }
        });

      });
  }

  handleShouldSaveAsDefaultChange() {
    this.setState({
      shouldSaveAsDefault: !this.state.shouldSaveAsDefault
    });
  }

  handleCreateLinkClick() {
    this.handleCreateLink();
  }

  handleAskForVisitorInfoChange(event) {
    this.setState({
      askForVisitorInfo: event.target.checked
    });
  }

  handleSendNotificationsChange(event) {
    this.setState({
      sendNotifications: event.target.checked
    });
  }

}


LinkCreator.propTypes = {
  page: PropTypes.instanceOf(Page).isRequired,
  showShareHelpText: PropTypes.bool,
  initialAskForVisitorInfo: PropTypes.bool,
  initialSendNotifications: PropTypes.bool,
  onRequestClose: PropTypes.func.isRequired,
  onRouteCreate: PropTypes.func,
  onInitialSettingsSave: PropTypes.func.isRequired,
  onViewLinksClick: PropTypes.func.isRequired,
  onSubscribeToRouteClick: PropTypes.func.isRequired
};

LinkCreator.defaultProps = {
  initialAskForVisitorInfo: false,
  initialSendNotifications: false,
  showShareHelpText: false,
  showDefaultsCheckbox: false
};

export default LinkCreator;
